import { clamp, cloneDeep, isNumber, toInteger } from 'lodash';
import PptxGenJS from '@meitu/js-to-pptx';
import IconFont from '@meitu/xdesign-icon-font';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import {
    createCanvas,
    createForeignObjectImageUrl,
    fetchVectorText,
    imageConverter,
    isHttpUri,
    loadSvgAsync,
    serializerDomToString,
} from '@/utils/file-kit';
import { deleteExtraInfo, getLayerFreshDOM } from '@/utils/layer-kit';
import { pxToInch } from '@/utils/unit-kit';
import {
    BrushType,
    IActiveBaseInfo,
    IBrushLayerProps,
    IExportBehavior,
    ILayerBehavior,
    ILayerOriginInfo,
    ILayerProps,
    IRenderLayerExtraProps,
    ISize,
    LayerType,
} from '@/version-types';
import {
    BRUSH_POINT_SIZE,
    generateLinePath,
    getDefaultBrushAttribute,
    getDefaultBrushPoint,
    getPathOffset,
    getScreenshotUrlCode,
    isCodeSvg,
    LAYER_EDGE_GAP,
    uploadBrushFile,
} from './utils';
import ActiveOperate from './views/active-operate';
import BrushLayer from './views/brush-layer';
import { renderDom } from './views/render-dom';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <BrushLayer layer={layer} {...extraProps} />;
        },

        async onBeforeAddOrUpdate(layer) {
            const data = layer.data as IBrushLayerProps;
            // 补全被客户端丢失的默认值
            if (!data.stroke) {
                data.stroke = getDefaultBrushAttribute();
            }
            if (!data.startPoint) {
                data.startPoint = getDefaultBrushPoint();
            }
            if (!data.startPoint.offset) {
                data.startPoint.offset = getDefaultBrushPoint().offset;
            }
            if (!data.endPoint) {
                data.endPoint = getDefaultBrushPoint();
            }
            if (!data.endPoint.offset) {
                data.endPoint.offset = getDefaultBrushPoint().offset;
            }
            if (data.screenshotUrl && isHttpUri(data.screenshotUrl)) {
                const code = await getScreenshotUrlCode(identity, layer, true);
                data.screenshotUrl = code;
            }
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IBrushLayerProps;
            deleteExtraInfo(layer);
            const { width, height } = layer;

            // 检查曲线资源是否存在
            if (data.brushType === BrushType.Curve && !data.url && data.__drawPath) {
                const res = await uploadBrushFile(data.__drawPath, data.naturalWidth, data.naturalHeight);
                if (res.url) {
                    data.url = res.url;
                }
            }

            if (width && height) {
                // 上传缩略图
                const code = await getScreenshotUrlCode(identity, layer);

                // 获取远程缩略图地址
                data.screenshotUrl = await base64Kit.getRemoteUrlAsync(code);
            } else {
                data.screenshotUrl = '';
            }

            // console.log(layer.data);
        },

        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            // console.time('===========生成时间');
            const { width, height, left, top, opacity, rotate } = layer;
            const { width: stageWidth, height: stageHeight } = resultSize;
            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const centerPoint = {
                x: resultWidth / 2 + left,
                y: resultHeight / 2 + top,
            };
            const drawOffsetPoint = {
                x: -resultWidth / 2,
                y: -resultHeight / 2,
            };
            const layerImage = await layerBehavior.getLayerImageAsync?.(layer, false);
            if (!layerImage) return;
            const { canvas: destCanvas, ctx: destCtx } = createCanvas(stageWidth, stageHeight);
            destCtx.translate(centerPoint.x, centerPoint.y);
            destCtx.rotate((Math.PI / 180) * rotate);
            // 透明度
            destCtx.globalAlpha = opacity;
            destCtx.drawImage(
                layerImage,
                0,
                0,
                resultWidth,
                resultHeight,
                drawOffsetPoint.x,
                drawOffsetPoint.y,
                resultWidth,
                resultHeight,
            );
            // console.timeEnd('===========生成时间');
            return destCanvas;
        },
        async getLayerImageAsync(layer: ILayerProps, globalAlpha = true) {
            // console.time('===========生成时间');
            const { width, height, opacity } = layer;
            const {
                url,
                // brushType,
                // naturalWidth,
                // naturalHeight,
                imageTransform: {
                    flipH,
                    flipV,
                    scale,
                    rotate: imgRotate,
                    widthRatio,
                    heightRatio,
                    offsetX: offsetXRatio,
                    offsetY: offsetYRatio,
                },
                startPoint,
                endPoint,
            } = layer.data as IBrushLayerProps;

            // 加载 svg 内容，为合成做准备
            let loadSvgError = false;
            try {
                if (startPoint.url) {
                    await fetchVectorText(startPoint.url);
                }
                if (endPoint.url) {
                    await fetchVectorText(endPoint.url);
                }
                if (url) {
                    await fetchVectorText(url);
                }
            } catch (error) {
                console.log('load svg error', error);
                loadSvgError = true;
            }
            const selector = `[data-draw-id="svg-${layer.id}"]`;
            const removeDomHandle = await renderDom(identity, layer);
            let baseImage = getLayerFreshDOM(selector) as HTMLImageElement;
            if (!baseImage || loadSvgError) {
                // 获取 html 异常，返回一个空画布
                removeDomHandle?.();
                return createCanvas(1, 1).canvas;
            }

            const svgDomUrl = createForeignObjectImageUrl(width, height, serializerDomToString(baseImage));
            baseImage = (await loadSvgAsync(svgDomUrl)) as HTMLImageElement;

            // 绘制描边
            const strokeCanvas = document.querySelector(
                `[data-stroke-id=svg-stroke-${layer.id}]`,
            ) as unknown as HTMLCanvasElement;

            if (strokeCanvas) {
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(strokeCanvas.width, strokeCanvas.height);
                destCtx.drawImage(strokeCanvas, 0, 0);
                destCtx.drawImage(baseImage, 0, 0);
                baseImage = destCanvas as unknown as HTMLImageElement;
            }

            const resultWidth = toInteger(width);
            const resultHeight = toInteger(height);
            const offsetX = toInteger(offsetXRatio * widthRatio * width * scale);
            const offsetY = toInteger(offsetYRatio * heightRatio * height * scale);
            const originImgWidth = toInteger(width * widthRatio);
            const originImgHeight = toInteger(height * heightRatio);
            let imgOffsetX = resultWidth / 2;
            let imgOffsetY = resultHeight / 2;
            if (flipH) {
                imgOffsetX = imgOffsetX - offsetX;
            } else {
                imgOffsetX = imgOffsetX + offsetX;
            }
            if (flipV) {
                imgOffsetY = imgOffsetY - offsetY;
            } else {
                imgOffsetY = imgOffsetY + offsetY;
            }
            // 将图层绘制到画布上
            const getLayerResult = async (layerCanvas: HTMLCanvasElement) => {
                const finalCanvas = layerCanvas;
                if (!globalAlpha) return finalCanvas;
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(finalCanvas.width, finalCanvas.height);
                destCtx.globalAlpha = opacity;
                destCtx.drawImage(finalCanvas, 0, 0, finalCanvas.width, finalCanvas.height);
                return destCanvas;
            };

            //  绘制图层
            const getLayerCanvas = async () => {
                const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);
                destCtx.translate(imgOffsetX, imgOffsetY);
                let rotate = imgRotate;
                if (flipH) {
                    rotate = -rotate;
                }
                if (flipV) {
                    rotate = -rotate;
                }
                destCtx.rotate((rotate * Math.PI) / 180);
                destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
                destCtx.drawImage(
                    baseImage,
                    -originImgWidth / 2,
                    -originImgHeight / 2,
                    originImgWidth,
                    originImgHeight,
                );
                return destCanvas;
            };

            const layerCanvas = await getLayerCanvas();
            const result = await getLayerResult(layerCanvas);
            removeDomHandle?.();
            return result;
        },

        async savePPTLayerAsync(slide, layer) {
            const { left, top, width, height, rotate } = layer;
            const options: PptxGenJS.ImageProps = {
                // path: url,
                x: pxToInch(left),
                y: pxToInch(top),
                w: pxToInch(width),
                h: pxToInch(height),
                rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
            };

            const canvas = await layerBehavior.getLayerImageAsync?.(layer);

            options.data = canvas?.toDataURL() || '';

            slide.addImage(options);
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            /**
             * 箭头、直线 在直线操作里限制
             * 这里只限制通用的操作
             */
            const data = layer.data as IBrushLayerProps;
            const isCode = isCodeSvg(data.brushType);
            if (isCode) {
                return {
                    cutWidth: Math.max(1, data.stroke.width * 2),
                    cutHeight: Math.max(1, data.stroke.width * 2),
                    radius: data.radius || 0,
                    stroke: cloneDeep(data.stroke),
                };
            }

            if (data.brushType === BrushType.Line) {
                return {
                    cutWidth: data.stroke.width * 2 + LAYER_EDGE_GAP + 1,
                    cutHeight: data.stroke.width * 2 + LAYER_EDGE_GAP + 1,
                    stroke: cloneDeep(data.stroke),
                };
            }

            if (data.brushType === BrushType.Arrow) {
                const startPoint = data.startPoint;
                const endPoint = data.endPoint;

                return {
                    cutWidth: Math.ceil(
                        Math.max(startPoint.width / BRUSH_POINT_SIZE || 1, endPoint.width / BRUSH_POINT_SIZE || 1),
                    ),
                    cutHeight: Math.ceil(
                        Math.max(startPoint.height / BRUSH_POINT_SIZE || 1, endPoint.height / BRUSH_POINT_SIZE || 1),
                    ),
                };
            }

            if (data.brushType === BrushType.Wavy) {
                return {
                    cutWidth: data.stroke.width * 2,
                    cutHeight: data.naturalHeight,
                };
            }

            const res = getPathOffset(data.fills, data.stroke);
            return {
                cutWidth: res.offsetWidth + 1,
                cutHeight: res.offsetHeight + 1,
                fills: cloneDeep(data.fills),
                stroke: cloneDeep(data.stroke),
            };
        },
        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, height, cutHeight, cutWidth } = originInfo;

            const data = layer.data as IBrushLayerProps;
            if (
                data.brushType === BrushType.Line ||
                data.brushType === BrushType.Arrow ||
                data.brushType === BrushType.Wavy
            ) {
                const offHeight = height - cutHeight;
                return (height - offHeight) / height;
            }

            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        onBeforeScaleUpdate(layer: ILayerProps, { originInfo }) {
            const data = layer.data as IBrushLayerProps;
            const originWidth = originInfo?.width || 0;
            const ratio = layer.width / originWidth;

            const stroke = originInfo?.stroke;
            if (stroke) {
                data.stroke.width = Math.ceil(stroke.width * ratio);
                data.stroke.offsetX = Math.ceil(stroke.offsetX * ratio);
                data.stroke.offsetY = Math.ceil(stroke.offsetY * ratio);
            }

            if (data.brushType === BrushType.Line) {
                const width = clamp(layer.height - LAYER_EDGE_GAP * 2 - data.stroke.width * 2, 1, layer.height);
                data.fills[0].width = width;
                const fillWidth = layer.width - LAYER_EDGE_GAP * 2 - data.stroke.width * 2;
                data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: fillWidth, y: 0 });
                data.naturalWidth = fillWidth;
            }
            if (data.brushType === BrushType.Arrow) {
                const startPoint = data.startPoint;
                const endPoint = data.endPoint;
                const startWidth = ~~((BRUSH_POINT_SIZE / startPoint.height) * layer.height);
                const endWidth = ~~((BRUSH_POINT_SIZE / endPoint.height) * layer.height);
                data.fills[0].width = Math.max(startWidth, endWidth, 1);
            }
            if (data.brushType === BrushType.Wavy) {
                console.log(originInfo);
            }
            if (data.brushType === BrushType.Rect) {
                if (isNumber(data.radius) && isNumber(originInfo?.radius)) {
                    data.radius = Math.floor(originInfo.radius * ratio);
                }
            }
            if (data.brushType === BrushType.Curve) {
                const fills = originInfo?.fills;
                if (fills?.length) {
                    fills.forEach((item, index) => {
                        data.fills[index].width = Math.ceil(item.width * ratio);
                        data.fills[index].offsetX = Math.ceil(item.offsetX * ratio);
                        data.fills[index].offsetY = Math.ceil(item.offsetY * ratio);
                    });
                }
            }
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { brushType } = layer.data as IBrushLayerProps;
            const { $t } = getLocaleStore();
            if (brushType === BrushType.Line || brushType === BrushType.Arrow) {
                return $t((v) => v.brushLayer.Behavior.line);
            }
            if (brushType === BrushType.Curve || brushType === BrushType.Wavy) {
                return $t((v) => v.brushLayer.Behavior.curve);
            }
            return $t((v) => v.brushLayer.Behavior.shape);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            let { screenshotUrl } = layer.data as IBrushLayerProps;
            if (screenshotUrl) {
                screenshotUrl = base64Kit.getRemoteUrl(screenshotUrl);
            }
            return (
                <div className="l-layer__icon border">
                    {screenshotUrl ? (
                        <img src={imageConverter(screenshotUrl, 32, 100)} alt="" />
                    ) : (
                        <IconFont name="BrushAndBoard" fontSize={16} />
                    )}
                </div>
            );
        },

        customActiveOperate(layer) {
            const brushType = (layer.data as IBrushLayerProps).brushType;
            if (brushType === BrushType.Line || brushType === BrushType.Arrow || brushType === BrushType.Wavy) {
                return <ActiveOperate layer={layer} />;
            }

            return null;
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.BRUSH_LAYER,
    getBehavior,
};

export default exportBehavior;
